import { getRandomUuid } from "./browserApis";
import { noteDegradation } from "./degradations";

const STORAGE_KEY = "bt-sawmillCorrelationId";

/**
 * Fallback id for when sessionStorage is unreachable.
 *
 * Without this the degraded path would mint a fresh id per call, which keeps
 * events flowing but destroys the grouping the field exists for — every event
 * would look like its own session. Module-scoped, so it lasts exactly as long
 * as the page does, which is the same lifetime sessionStorage would have given
 * it.
 */
let inMemoryId: string | null = null;

export function getCorrelationId(): string {
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const id = getRandomUuid();
    window.sessionStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    // Storage access throws rather than returning null when the browser blocks
    // it — Safari private browsing, third-party-frame partitioning, a hardened
    // webview. This runs inside buildEvent for every event and `track` is
    // fire-and-forget, so letting it escape is 100% silent analytics loss for
    // the whole session.
    noteDegradation("session-storage");
    inMemoryId ??= getRandomUuid();
    return inMemoryId;
  }
}

/**
 * Forgets the current tab's correlation id so the next `getCorrelationId()`
 * mints a fresh one. Called on identity change — see `resetAnalyticsIdentity`.
 */
export function clearCorrelationId(): void {
  // Clear the memory fallback first and unconditionally: if storage is blocked,
  // removeItem throws, and leaving this set would keep the previous user's id
  // attached to every event after a logout.
  inMemoryId = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to remove — the id being cleared was the in-memory one above.
  }
}
